import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { RefillSummarySectionData } from "slices/refillSummarySelectors";
import { createSplitDishName } from "utils/utils";
import { OrderListDishTypeItem } from "./OrderListDishTypeItem";
import { StyledDishTypeList } from "./StyledDishTypeList";

type RefillSummarySectionProps = {
	variant: "added" | "removed";
	section: RefillSummarySectionData;
};

export const RefillSummarySection = ({ variant, section }: RefillSummarySectionProps) => {
	const { t } = useTranslation();
	const labelKey = variant === "added" ? "order.refillSummary.added" : "order.refillSummary.removed";

	return (
		<Box
			flex={1}
			minHeight={0}
			width="100%"
			display="flex"
			flexDirection="column"
			gap={1}
			data-testid={`refill-summary-${variant}`}
		>
			{/* `data-total` carries the number for tests: the shared i18n mock returns the raw key. */}
			<Typography
				variant="mealMain"
				color="white"
				data-testid={`refill-summary-${variant}-header`}
				data-total={String(section.total)}
			>
				{t(labelKey, { pieces: section.total })}
			</Typography>
			{section.rows.length === 0 ? (
				<Typography
					data-testid={`refill-summary-${variant}-empty`}
					variant="mealSub"
					color="white"
					sx={{ opacity: 0.6 }}
				>
					{t("order.refillSummary.empty")}
				</Typography>
			) : (
				<StyledDishTypeList
					data-testid={`refill-summary-${variant}-list`}
					sx={{ height: "auto", minHeight: 0, flex: 1 }}
				>
					{/* The order summary's row without the price; a type the terminal does not know gets a name too. */}
					{section.rows.map((row) => (
						<OrderListDishTypeItem
							key={`refill_summary_${variant}_${row.dishTypeId ?? "unknown"}`}
							data-testid="refill-summary-row"
							data-dish-type-id={row.dishTypeId ?? "unknown"}
							name={createSplitDishName(row.name) ?? { header: t("order.refillSummary.unknownDish"), subHeader: "" }}
							pieces={row.pieces}
							dishType={{ id: row.dishTypeId ?? "unknown", image: row.image }}
							showPrice={false}
							// The 84 px order row is fixed-height; a wrapped name plus its subtitle may need more.
							sx={{ height: "auto", minHeight: "84px" }}
						/>
					))}
				</StyledDishTypeList>
			)}
		</Box>
	);
};
